import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { trackUserSignup } from "@/utils/analytics";
import { getAttributionForSignup } from "@/utils/utm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RegisterPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: searchParams.get("role") || "student",
    ageConfirmed: false,
    termsAccepted: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const roleParam = searchParams.get("role");
    if (roleParam) {
      setFormData(prev => ({ ...prev, role: roleParam }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    if (!formData.ageConfirmed) {
      setError("Please confirm you are 13 or older. Under 13? Use a class code from your teacher.");
      setLoading(false);
      return;
    }

    if (!formData.termsAccepted) {
      setError("Please accept the Terms of Service and Student Privacy Policy to continue.");
      setLoading(false);
      return;
    }

    try {
      const { confirmPassword, ageConfirmed, termsAccepted, ...registerData } = formData;
      registerData.age_confirmed_13_plus = ageConfirmed;
      registerData.terms_accepted = termsAccepted;
      // Attach first-touch marketing attribution if captured on landing.
      const attribution = getAttributionForSignup();
      if (attribution) registerData.attribution = attribution;
      const response = await axios.post(`${API}/auth/register`, registerData);
      
      // Track Meta Pixel CompleteRegistration event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration', {
          content_name: formData.role,
          status: 'success'
        });
      }
      
      trackUserSignup({ role: formData.role, method: "email" });
      
      onLogin(response.data.user, response.data.access_token);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="container mx-auto px-4 py-16 flex items-center justify-center min-h-screen">
        <div className="auth-card w-full max-w-md p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/assets/lsbp-logo.png" 
              alt="The Lemonade Stand"
              className="mx-auto mb-4"
              style={{ maxWidth: "150px", height: "auto" }}
            />
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Join The Adventure!</h1>
            <p className="text-gray-600">Create your account to get started</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="error-message">
              {error}
            </div>
          )}

          {/* Register Form */}
          <form onSubmit={handleSubmit} data-testid="register-form">
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2">Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                data-testid="role-select"
              >
                <option value="student">Student</option>
                <option value="educator">Educator</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2">Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="John Doe"
                required
                data-testid="name-input"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="your@email.com"
                required
                data-testid="email-input"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="••••••••"
                required
                minLength="6"
                data-testid="password-input"
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-2">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="••••••••"
                required
                data-testid="confirm-password-input"
              />
            </div>

            {/* COPPA age gate — required attestation */}
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="ageConfirmed"
                  checked={formData.ageConfirmed}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 accent-yellow-500 shrink-0"
                  data-testid="age-confirm-checkbox"
                />
                <span className="text-sm text-gray-800">
                  <strong>I am 13 or older.</strong>{" "}
                  <span className="text-gray-600">
                    Under 13? Don&apos;t create your own account —{" "}
                    <Link to="/student-login" className="text-yellow-700 font-semibold underline">
                      use your teacher&apos;s class code
                    </Link>{" "}
                    instead.
                  </span>
                </span>
              </label>
            </div>

            {/* Terms + Privacy acceptance */}
            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 accent-yellow-500 shrink-0"
                  data-testid="terms-accept-checkbox"
                />
                <span className="text-sm text-gray-700">
                  I have read and agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-yellow-700 font-semibold underline">Terms of Service</Link>
                  {", "}
                  <Link to="/privacy" target="_blank" className="text-yellow-700 font-semibold underline">Privacy Policy</Link>
                  {", and "}
                  <Link to="/student-privacy" target="_blank" className="text-yellow-700 font-semibold underline">Student Data Privacy Policy</Link>.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="lemon-button w-full py-3 rounded-lg text-white font-bold text-lg disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>

          {/* Login Link */}
          <div className="text-center mt-6">
            <p className="text-gray-600">
              Already have an account?{" "}
              <Link to="/login" className="text-yellow-600 hover:text-yellow-700 font-semibold" data-testid="login-link">
                Sign In
              </Link>
            </p>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700" data-testid="back-home-link">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
